// FieldVision - MarkDropdown.tsx
// Mark-vaelger dropdown med thumbnail, navn og areal
// Viser valgt mark og dropdown med andre marker

import React, { useState, useRef, useEffect } from 'react';

interface Field {
  id: string;
  marknr: string;
  areal: number;
  cvr: string;
  afgrode?: string;
  mapCount?: number;
}

interface MarkDropdownProps {
  currentField: Field | null;
  fields: Field[];
  onFieldChange?: (fieldId: string) => void;
}

const MarkDropdown: React.FC<MarkDropdownProps> = ({
  currentField,
  fields,
  onFieldChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Luk dropdown ved klik udenfor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFieldSelect = (fieldId: string) => {
    setIsOpen(false);
    if (onFieldChange) {
      onFieldChange(fieldId);
    }
  };

  // Filtrer andre marker (ikke den aktuelle)
  const otherFields = fields.filter(f => f.id !== currentField?.id);

  if (!currentField) {
    return (
      <div className="mark-dropdown-empty">
        <p>Ingen mark valgt</p>
      </div>
    );
  }

  return (
    <div className="mark-dropdown" ref={dropdownRef}>
      {/* Dropdown button - viser valgt mark */}
      <button
        className={`mark-dropdown-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="mark-dropdown-left">
          {/* Thumbnail placeholder */}
          <div className="mark-thumbnail">
            <svg viewBox="0 0 40 40" className="mark-icon">
              <polygon
                points="20,5 35,15 35,30 20,35 5,30 5,15"
                fill="#22c55e"
                opacity="0.3"
                stroke="#16a34a"
                strokeWidth="2"
              />
            </svg>
          </div>
          <div className="mark-info">
            <span className="mark-name">{currentField.marknr}</span>
            <span className="mark-areal">{currentField.areal?.toFixed(1) || '0.0'} ha</span>
          </div>
        </div>
        <div className="mark-dropdown-right">
          {currentField.mapCount && currentField.mapCount > 0 && (
            <span className="map-count-badge">{currentField.mapCount} kort</span>
          )}
          <span className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}>
            &#9660;
          </span>
        </div>
      </button>

      {/* Dropdown liste */}
      {isOpen && otherFields.length > 0 && (
        <div className="mark-dropdown-list">
          {otherFields.map(field => (
            <button
              key={field.id}
              className="mark-dropdown-item"
              onClick={() => handleFieldSelect(field.id)}
            >
              <div className="mark-dropdown-left">
                <div className="mark-thumbnail small">
                  <svg viewBox="0 0 40 40" className="mark-icon">
                    <polygon
                      points="20,5 35,15 35,30 20,35 5,30 5,15"
                      fill="#94a3b8"
                      opacity="0.3"
                      stroke="#64748b"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="mark-info">
                  <span className="mark-name">{field.marknr}</span>
                  <span className="mark-areal">{field.areal?.toFixed(1) || '0.0'} ha</span>
                </div>
              </div>
              {field.mapCount && field.mapCount > 0 && (
                <span className="map-count-badge">{field.mapCount} kort</span>
              )}
            </button>
          ))}
        </div>
      )}

      <style>{`
        .mark-dropdown {
          position: relative;
        }

        .mark-dropdown-empty {
          padding: 12px 16px;
          background: #f9fafb;
          border: 1px dashed #e5e7eb;
          border-radius: 12px;
          text-align: center;
          color: #9ca3af;
        }

        .mark-dropdown-btn {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 12px 16px;
          background: white;
          border: 2px solid #16a34a;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mark-dropdown-btn:hover {
          background: #f0fdf4;
        }

        .mark-dropdown-btn.open {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        .mark-dropdown-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mark-dropdown-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mark-thumbnail {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: #f0fdf4;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .mark-thumbnail.small {
          width: 32px;
          height: 32px;
          background: #f1f5f9;
        }

        .mark-icon {
          width: 100%;
          height: 100%;
        }

        .mark-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .mark-name {
          font-weight: 600;
          font-size: 15px;
          color: #111827;
        }

        .mark-areal {
          font-size: 13px;
          color: #6b7280;
        }

        .map-count-badge {
          padding: 4px 8px;
          background: #f0fdf4;
          color: #16a34a;
          font-size: 12px;
          font-weight: 500;
          border-radius: 4px;
        }

        .dropdown-arrow {
          font-size: 10px;
          color: #9ca3af;
          transition: transform 0.2s;
        }

        .dropdown-arrow.rotated {
          transform: rotate(180deg);
        }

        .mark-dropdown-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 2px solid #16a34a;
          border-top: 1px solid #e5e7eb;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
          max-height: 240px;
          overflow-y: auto;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .mark-dropdown-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 10px 16px;
          background: white;
          border: none;
          cursor: pointer;
          transition: background 0.15s;
          text-align: left;
        }

        .mark-dropdown-item:hover {
          background: #f0fdf4;
        }

        .mark-dropdown-item:not(:last-child) {
          border-bottom: 1px solid #f3f4f6;
        }

        .mark-dropdown-item .mark-name {
          font-size: 14px;
        }

        .mark-dropdown-item .mark-areal {
          font-size: 12px;
        }

        .mark-dropdown-item .map-count-badge {
          font-size: 11px;
          padding: 3px 6px;
        }
      `}</style>
    </div>
  );
};

export default MarkDropdown;
